import { useEffect, useState } from 'react';
import { Stack } from '@mui/material';
import { useDropzone } from 'react-dropzone';

import { CroppedReplaceableImage, ImageUploadArea, SnackbarError } from '@/components';

export const ImageUploadUtil = ({
  getUploadedImage,
  initialImageSrc,
}: {
  getUploadedImage?: (image: File) => void;
  initialImageSrc?: string;
}) => {
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const [fileSizeError, setFileSizeError] = useState(false);
  const [unsupportedTypeError, setUnsupportedTypeError] = useState(false);

  useEffect(() => {
    setCroppedImage(initialImageSrc || null);
  }, [initialImageSrc]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'image/png': ['.png'], 'image/jpg': ['.jpg'], 'image/jpeg': ['.jpeg'] },
    maxSize: 3 * 1024 * 1024, // 3MB
    onDrop: (acceptedFiles, rejectedFiles) => {
      const hasUnsupportedType = rejectedFiles.some(
        (file) => !['image/jpeg', 'image/jpg', 'image/png'].includes(file.file.type),
      );

      if (hasUnsupportedType) {
        setUnsupportedTypeError(true);
        return;
      }

      if (acceptedFiles.length > 0) {
        if (getUploadedImage) {
          getUploadedImage(acceptedFiles[0]);
        }
        setCroppedImage(acceptedFiles[0] ? URL.createObjectURL(acceptedFiles[0]) : null);
        setFileSizeError(false);
        setUnsupportedTypeError(false);
      }
    },
    onDropRejected: () => {
      setFileSizeError(true);
    },
  });

  const handleCloseError = () => {
    setFileSizeError(false);
    setUnsupportedTypeError(false);
  };

  const handleChangePictureClick = () => {
    setCroppedImage(null);
  };

  return (
    <Stack gap={2}>
      {!croppedImage && (
        <ImageUploadArea
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          truncatedForm={isDragActive}
        />
      )}

      <SnackbarError
        open={fileSizeError}
        onClose={handleCloseError}
        alertMessage={'Soubor je příliš velký. Maximální velikost je 3MB.'}
      />

      <SnackbarError
        open={unsupportedTypeError}
        onClose={handleCloseError}
        alertMessage={'Nepodporovaný typ souboru. Povolené formáty jsou JPEG a PNG.'}
      />

      {croppedImage && (
        <CroppedReplaceableImage
          croppedImageSrc={croppedImage}
          handleChangePictureClick={handleChangePictureClick}
        />
      )}
    </Stack>
  );
};
